/* KST Multi Select (React 19) - Mobile Modal + Desktop Dropdown
   Similar to Kendo MultiSelect but as a React component.
   Used for selecting multiple items like Violation Types.
   Converted from Vue 2 KstMultiSelect component.
*/
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  memo,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';

export interface MultiSelectItem {
  [key: string]: any;
}

export interface MultiSelectProps {
  value: any[];
  items: MultiSelectItem[];
  labelKey?: string;
  valueKey?: string;
  groupBy?: string | null;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  maxDisplay?: number;
  sortItems?: boolean;
  onChange?: (values: any[]) => void;
  onChangeItems?: (values: any[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export const MultiSelect = memo(function MultiSelect({
  value = [],
  items = [],
  labelKey = 'label',
  valueKey = 'value',
  groupBy = null,
  placeholder = 'Select...',
  disabled = false,
  loading = false,
  maxDisplay = 3,
  sortItems = true,
  onChange,
  onChangeItems,
  onOpen,
  onClose,
}: MultiSelectProps) {
  const { theme } = useTheme();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeFlatIndex, setActiveFlatIndex] = useState(-1);
  const [isMobile, setIsMobile] = useState(false);
  const [justOpened, setJustOpened] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<Record<string, string>>({});
  const [dropdownFlipped, setDropdownFlipped] = useState(false);
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);

  // Refs
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const activeRowRef = useRef<HTMLDivElement>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollYRef = useRef(0);
  const lastTouchYRef = useRef<number | null>(null);
  const justOpenedRef = useRef(false);
  const openRef = useRef(false);
  const isKeyboardNavRef = useRef(false);

  // Keep refs in sync
  openRef.current = open;
  justOpenedRef.current = justOpened;
  isKeyboardNavRef.current = isKeyboardNav;

  // ---------- Computed values ----------

  const selectedItems = useMemo(() => {
    if (!items || items.length === 0 || !value || value.length === 0) return [];
    const result: MultiSelectItem[] = [];
    for (let i = 0; i < items.length; i++) {
      if (value.indexOf(items[i][valueKey]) !== -1) {
        result.push(items[i]);
      }
    }
    return result;
  }, [items, value, valueKey]);

  const filtered = useMemo(() => {
    if (!items || items.length === 0) return [];
    const q = (query || '').trim().toLowerCase();
    if (!q) return items.slice();
    const terms = q.split(/\s+/).filter((t: string) => t.length > 0);
    if (terms.length === 0) return items.slice();
    return items.filter((it: MultiSelectItem) => {
      const lbl = String(it[labelKey] || '').toLowerCase();
      const grp = groupBy ? String(it[groupBy] || '').toLowerCase() : '';
      const combined = lbl + ' ' + grp;
      for (let i = 0; i < terms.length; i++) {
        if (combined.indexOf(terms[i]) === -1) return false;
      }
      return true;
    });
  }, [items, query, labelKey, groupBy]);

  const grouped = useMemo((): { name: string; items: MultiSelectItem[] }[] => {
    if (!filtered || filtered.length === 0) return [];

    const shouldSort = sortItems;

    // If no groupBy, return single group
    if (!groupBy) {
      const sortedItems = filtered.slice();
      if (shouldSort) {
        sortedItems.sort((a: MultiSelectItem, b: MultiSelectItem) => {
          const la = String(a[labelKey] || '');
          const lb = String(b[labelKey] || '');
          return la.localeCompare(lb, undefined, { numeric: true, sensitivity: 'base' });
        });
      }
      return [{ name: '', items: sortedItems }];
    }

    const map: Record<string, MultiSelectItem[]> = {};
    for (let i = 0; i < filtered.length; i++) {
      const it = filtered[i];
      const key = it[groupBy] || 'Other';
      if (!map[key]) map[key] = [];
      map[key].push(it);
    }

    const groups = Object.keys(map).sort();
    const out: { name: string; items: MultiSelectItem[] }[] = [];
    for (let g = 0; g < groups.length; g++) {
      const name = groups[g];
      const arr = map[name].slice();
      if (shouldSort) {
        arr.sort((a: MultiSelectItem, b: MultiSelectItem) => {
          const la = String(a[labelKey] || '');
          const lb = String(b[labelKey] || '');
          return la.localeCompare(lb, undefined, { numeric: true, sensitivity: 'base' });
        });
      }
      out.push({ name, items: arr });
    }
    return out;
  }, [filtered, sortItems, groupBy, labelKey]);

  const flatVisible = useMemo(() => {
    const acc: MultiSelectItem[] = [];
    for (let i = 0; i < grouped.length; i++) {
      for (let j = 0; j < grouped[i].items.length; j++) {
        acc.push(grouped[i].items[j]);
      }
    }
    return acc;
  }, [grouped]);

  const displayText = useMemo(() => {
    if (!selectedItems || selectedItems.length === 0) return '';
    if (selectedItems.length <= maxDisplay) {
      return selectedItems.map((it: MultiSelectItem) => it[labelKey]).join(', ');
    }
    return selectedItems.slice(0, maxDisplay).map((it: MultiSelectItem) => it[labelKey]).join(', ') +
      ' (+' + (selectedItems.length - maxDisplay) + ' more)';
  }, [selectedItems, maxDisplay, labelKey]);

  const isSelectedFn = useCallback((item: MultiSelectItem): boolean => {
    if (!value || !Array.isArray(value)) return false;
    return value.indexOf(item[valueKey]) !== -1;
  }, [value, valueKey]);

  const hasValue = selectedItems && selectedItems.length > 0;

  // ---------- Mobile check ----------

  useEffect(() => {
    setIsMobile(
      /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768
    );
  }, []);

  // ---------- Portal management (desktop only) ----------

  const cleanupPortal = useCallback(() => {
    if (portalContainerRef.current && portalContainerRef.current.parentNode === document.body) {
      document.body.removeChild(portalContainerRef.current);
    }
    portalContainerRef.current = null;
  }, []);

  const ensurePortalContainer = useCallback(() => {
    if (!portalContainerRef.current) {
      const container = document.createElement('div');
      container.className = 'kst-ms-portal' + (theme ? ' ' + theme : '');
      container.style.cssText = 'position: fixed; z-index: 9999;';

      const innerWrapper = document.createElement('div');
      innerWrapper.className = 'kst-ms';
      container.appendChild(innerWrapper);
      document.body.appendChild(container);

      portalContainerRef.current = container;
    }
    return portalContainerRef.current;
  }, [theme]);

  // ---------- Dropdown position (desktop portal) ----------

  const dropdownRafRef = useRef(0);
  const updateDropdownPosition = useCallback(() => {
    if (!openRef.current || !portalContainerRef.current) return;

    // rAF-throttle: cancel any pending frame to avoid queuing multiples
    if (dropdownRafRef.current) cancelAnimationFrame(dropdownRafRef.current);
    dropdownRafRef.current = requestAnimationFrame(() => {
      dropdownRafRef.current = 0;
      const control = rootRef.current?.querySelector('.kst-ms__control');
      if (!control) return;

      const rect = control.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const dropdownHeight = 350;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      let shouldFlip = false;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        shouldFlip = true;
      }

      setDropdownFlipped(shouldFlip);

      const newStyle: Record<string, string> = {};
      if (shouldFlip) {
        newStyle.position = 'fixed';
        newStyle.bottom = (viewportHeight - rect.top + 4) + 'px';
        newStyle.left = rect.left + 'px';
        newStyle.width = Math.max(rect.width, 300) + 'px';
        newStyle.zIndex = '9999';
        newStyle.top = 'auto';
      } else {
        newStyle.position = 'fixed';
        newStyle.top = (rect.bottom + 4) + 'px';
        newStyle.left = rect.left + 'px';
        newStyle.width = Math.max(rect.width, 300) + 'px';
        newStyle.zIndex = '9999';
        newStyle.bottom = 'auto';
      }
      setDropdownStyle(newStyle);
    });
  }, []);

  // ---------- Scroll active item into view ----------

  const scrollActiveIntoView = useCallback(() => {
    requestAnimationFrame(() => {
      const el = activeRowRef.current;
      if (!el) return;
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }, []);

  // ---------- Actions ----------

  const doClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Refs for stale closure prevention in event handlers
  const valueRef = useRef(value);
  valueRef.current = value;
  const flatVisibleRef = useRef(flatVisible);
  flatVisibleRef.current = flatVisible;
  const activeFlatIndexRef = useRef(activeFlatIndex);
  activeFlatIndexRef.current = activeFlatIndex;

  const toggleItem = useCallback((item: MultiSelectItem) => {
    const val = item[valueKey];
    const current = (valueRef.current && Array.isArray(valueRef.current)) ? valueRef.current.slice() : [];
    const idx = current.indexOf(val);

    if (idx === -1) {
      current.push(val);
    } else {
      current.splice(idx, 1);
    }

    onChange?.(current);
    onChangeItems?.(current);
  }, [valueKey, onChange, onChangeItems]);

  const removeItem = useCallback((item: MultiSelectItem, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;
    const val = item[valueKey];
    const current = (valueRef.current && Array.isArray(valueRef.current)) ? valueRef.current.filter((v: any) => v !== val) : [];
    onChange?.(current);
    onChangeItems?.(current);
  }, [valueKey, disabled, onChange, onChangeItems]);

  const clearAll = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;
    onChange?.([]);
    onChangeItems?.([]);
  }, [disabled, onChange, onChangeItems]);

  // ---------- Keyboard ----------

  const onKeyDownControl = useCallback((e: React.KeyboardEvent) => {
    const k = e.key;
    if (!openRef.current && (k === 'ArrowDown' || k === 'Enter' || k === ' ')) {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  const onKeyDownList = useCallback((e: React.KeyboardEvent) => {
    const k = e.key;
    const currentFlat = flatVisibleRef.current;
    const currentIdx = activeFlatIndexRef.current;

    if (k === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      return;
    }

    if (k === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      return;
    }

    if (!currentFlat.length) return;

    if (k === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      setIsKeyboardNav(true);
      if (currentIdx === -1) {
        setActiveFlatIndex(0);
      } else {
        setActiveFlatIndex(Math.min(currentFlat.length - 1, currentIdx + 1));
      }
    } else if (k === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setIsKeyboardNav(true);
      if (currentIdx === -1) {
        setActiveFlatIndex(currentFlat.length - 1);
      } else {
        setActiveFlatIndex(Math.max(0, currentIdx - 1));
      }
    } else if (k === 'Home') {
      e.preventDefault();
      e.stopPropagation();
      setIsKeyboardNav(true);
      setActiveFlatIndex(0);
    } else if (k === 'End') {
      e.preventDefault();
      e.stopPropagation();
      setIsKeyboardNav(true);
      setActiveFlatIndex(currentFlat.length - 1);
    } else if (k === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const item = currentFlat[currentIdx];
      if (item) toggleItem(item);
    }
  }, [toggleItem]);

  // Scroll active into view when activeFlatIndex changes
  useEffect(() => {
    if (open && activeFlatIndex >= 0) {
      scrollActiveIntoView();
    }
  }, [activeFlatIndex, open, scrollActiveIntoView]);

  // ---------- Toggle open ----------

  const toggleOpen = useCallback((e?: React.MouseEvent) => {
    if (disabled || loading) return;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!openRef.current) {
      // Emit global event to close datetime pickers when opening
      window.dispatchEvent(new CustomEvent('datetime-picker-opened', { detail: { source: 'kst-multi-select' } }));
    }
    setOpen((prev) => !prev);
  }, [disabled]);

  // ---------- Click outside ----------

  const onClickOutside = useCallback((e: MouseEvent | TouchEvent) => {
    if (!openRef.current) return;
    if (justOpenedRef.current) return;

    const target = e.target as Node;
    const inComponent = rootRef.current?.contains(target);

    // Check if clicking in mobile modal or desktop portal
    const mobile = window.innerWidth <= 768;
    if (mobile) {
      const modal = document.querySelector('.kst-ms__modal');
      const inModal = modal?.contains(target);
      if (!inComponent && !inModal) {
        setOpen(false);
      }
    } else {
      const inPortal = portalContainerRef.current?.contains(target);
      if (!inComponent && !inPortal) {
        setOpen(false);
      }
    }
  }, []);

  // ---------- Touch scroll chaining prevention ----------

  const resetTouchTracking = useCallback(() => {
    lastTouchYRef.current = null;
  }, []);

  const preventScrollChaining = useCallback((e: React.TouchEvent) => {
    const list = listRef.current;
    if (!list) return;

    const scrollTop = list.scrollTop;
    const scrollHeight = list.scrollHeight;
    const clientHeight = list.clientHeight;

    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight;

    const touchY = e.touches[0].clientY;
    if (lastTouchYRef.current === null) {
      lastTouchYRef.current = touchY;
      return;
    }

    const deltaY = lastTouchYRef.current - touchY;
    const isScrollingUp = deltaY < 0;
    const isScrollingDown = deltaY > 0;

    if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
      e.preventDefault();
    }

    lastTouchYRef.current = touchY;
  }, []);

  // ---------- Backdrop click ----------

  const onBackdropClick = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains('kst-ms__modal-backdrop')) {
      setOpen(false);
    }
  }, []);

  // ---------- Search input handler ----------

  const onSearchInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setActiveFlatIndex(-1);
  }, []);

  // ---------- Effect: open/close lifecycle ----------

  useEffect(() => {
    if (open) {
      onOpen?.();
      setJustOpened(true);
      justOpenedRef.current = true;

      setActiveFlatIndex(-1);

      if (!isMobile) {
        // Desktop: create portal, position, and focus
        ensurePortalContainer();

        requestAnimationFrame(() => {
          updateDropdownPosition();
          requestAnimationFrame(() => {
            searchRef.current?.focus();
          });
        });

        window.addEventListener('scroll', updateDropdownPosition, true);
        window.addEventListener('resize', updateDropdownPosition);
      }

      // Lock body scroll on mobile
      if (isMobile) {
        scrollYRef.current = window.scrollY || window.pageYOffset;
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + scrollYRef.current + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';
      }

      // Clear justOpened flag after delay
      const timer = setTimeout(() => {
        setJustOpened(false);
        justOpenedRef.current = false;
      }, 200);

      return () => clearTimeout(timer);
    } else {
      onClose?.();
      setQuery('');
      setActiveFlatIndex(-1);
      setDropdownFlipped(false);
      setJustOpened(false);
      justOpenedRef.current = false;

      if (!isMobile) {
        requestAnimationFrame(() => {
          cleanupPortal();
        });
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      }

      // Restore body scroll on mobile
      if (isMobile) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollYRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---------- Effect: register click-outside listeners on mount ----------

  useEffect(() => {
    const timer = setTimeout(() => {
      document.addEventListener('mouseup', onClickOutside as EventListener, true);
      document.addEventListener('touchend', onClickOutside as EventListener, { passive: true, capture: true });
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseup', onClickOutside as EventListener, true);
      document.removeEventListener('touchend', onClickOutside as EventListener, true);
    };
  }, [onClickOutside]);

  // ---------- Effect: cleanup on unmount ----------

  useEffect(() => {
    return () => {
      cleanupPortal();
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);

      // Ensure body scroll is restored
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Effect: update portal theme class ----------

  useEffect(() => {
    if (portalContainerRef.current) {
      portalContainerRef.current.classList.remove('theme-gray-dark', 'theme-blue-dark');
      if (theme) {
        portalContainerRef.current.classList.add(theme);
      }
    }
  }, [theme]);

  // ---------- Render list content (shared between desktop and mobile) ----------

  const renderListContent = () => {
    if (!grouped.length) {
      return (
        <div className="kst-ms__item kst-ms__item--empty" aria-disabled="true">
          No results
        </div>
      );
    }

    const nodes: React.ReactNode[] = [];
    let flatIdx = 0;

    for (let g = 0; g < grouped.length; g++) {
      const group = grouped[g];
      if (group.name) {
        nodes.push(
          <div key={'group-' + g} className="kst-ms__group">
            {group.name}
          </div>
        );
      }
      for (let j = 0; j < group.items.length; j++) {
        const item = group.items[j];
        const currentFlatIdx = flatIdx;
        const isSel = isSelectedFn(item);
        const isAct = currentFlatIdx === activeFlatIndex;

        nodes.push(
          <div
            key={item[valueKey] ?? 'item-' + currentFlatIdx}
            className={
              'kst-ms__item' +
              (isAct ? ' kst-ms__item--active' : '') +
              (isSel ? ' kst-ms__item--selected' : '')
            }
            aria-selected={isSel ? 'true' : 'false'}
            role="option"
            ref={isAct ? activeRowRef : undefined}
            onClick={() => toggleItem(item)}
            onMouseMove={() => {
              if (isKeyboardNavRef.current) {
                isKeyboardNavRef.current = false;
                setIsKeyboardNav(false);
                return;
              }
              if (activeFlatIndexRef.current !== currentFlatIdx) {
                setActiveFlatIndex(currentFlatIdx);
              }
            }}
          >
            <span className="kst-ms__label">{item[labelKey]}</span>
          </div>
        );
        flatIdx++;
      }
    }

    return nodes;
  };

  // ---------- Render tags ----------

  const renderTags = () => {
    if (!selectedItems || selectedItems.length === 0) return null;

    return selectedItems.map((item: MultiSelectItem) => (
      <span key={item[valueKey]} className="kst-ms__tag">
        <span className="kst-ms__tag-text">{item[labelKey]}</span>
        {!disabled && (
          <button
            className="kst-ms__tag-remove"
            type="button"
            aria-label={'Remove ' + item[labelKey]}
            onClick={(e) => removeItem(item, e)}
            onMouseDown={(e) => e.preventDefault()}
          >
            {'\u00D7'}
          </button>
        )}
      </span>
    ));
  };

  // ---------- Desktop dropdown (rendered into portal) ----------

  const desktopDropdown = !isMobile && open ? (
    <div
      className={'kst-ms__dropdown' + (dropdownFlipped ? ' kst-ms__dropdown--flipped' : '')}
      style={dropdownStyle}
      role="listbox"
    >
      {/* Search input */}
      <div className="kst-ms__search">
        <input
          ref={searchRef}
          className="kst-ms__search-input"
          value={query}
          type="text"
          placeholder="Search..."
          aria-label="Filter options"
          inputMode="text"
          onChange={onSearchInput}
          onKeyDown={onKeyDownList}
        />
      </div>
      {/* List */}
      <div
        className="kst-ms__list"
        ref={listRef}
        onKeyDown={onKeyDownList}
        onMouseLeave={() => setActiveFlatIndex(-1)}
      >
        {renderListContent()}
      </div>
    </div>
  ) : null;

  // Render the desktop dropdown into the portal container
  const desktopPortal = desktopDropdown && portalContainerRef.current
    ? createPortal(
        desktopDropdown,
        portalContainerRef.current.querySelector('.kst-ms') || portalContainerRef.current
      )
    : desktopDropdown; // Fallback: render inline before portal is ready

  // ---------- Mobile modal ----------

  const mobileModal = isMobile && open ? (
    <div className="kst-ms__modal-backdrop" onClick={onBackdropClick}>
      <div className="kst-ms__modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="kst-ms__modal-header">
          <div className="kst-ms__modal-title">{placeholder}</div>
          <button
            className="kst-ms__modal-close"
            type="button"
            aria-label="Close"
            onClick={doClose}
          >
            {'\u00D7'}
          </button>
        </div>
        {/* Modal Search */}
        <div className="kst-ms__modal-search">
          <input
            ref={searchRef}
            className="kst-ms__modal-search-input"
            value={query}
            type="text"
            placeholder="Search..."
            aria-label="Filter options"
            inputMode="text"
            onChange={onSearchInput}
            onKeyDown={onKeyDownList}
          />
        </div>
        {/* Modal List */}
        <div
          className="kst-ms__modal-list"
          ref={listRef}
          onTouchStart={resetTouchTracking}
          onTouchMove={preventScrollChaining}
          onTouchEnd={resetTouchTracking}
        >
          {renderListContent()}
        </div>
        {/* Done button */}
        <div className="kst-ms__modal-footer">
          <button
            className="kst-ms__done-btn"
            type="button"
            onClick={doClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ---------- Control classes ----------

  const effectiveDisabled = disabled || loading;

  const controlClasses = [
    'kst-ms__control',
    open ? 'kst-ms__control--open' : '',
    effectiveDisabled ? 'kst-ms__control--disabled' : '',
  ].filter(Boolean).join(' ');

  // ---------- Main render ----------

  return (
    <div className={`kst-ms${theme ? ' ' + theme : ''}`} ref={rootRef}>
      <div
        className={controlClasses}
        role="combobox"
        aria-expanded={open ? 'true' : 'false'}
        tabIndex={effectiveDisabled ? -1 : 0}
        onClick={(e) => {
          e.stopPropagation();
          if (!loading) toggleOpen(e);
        }}
        onKeyDown={onKeyDownControl}
      >
        <div className="kst-ms__value-container">
          {loading
            ? <div className="kst-ms__placeholder">Loading...</div>
            : hasValue
              ? <div className="kst-ms__tags">{renderTags()}</div>
              : <div className="kst-ms__placeholder">{placeholder}</div>
          }
        </div>
        <div className="kst-ms__indicators">
          {loading ? (
            <i className="fas fa-spinner fa-spin kst-ms__spinner" />
          ) : (
            <>
              {hasValue && !disabled && (
                <button
                  className="kst-ms__clear"
                  type="button"
                  aria-label="Clear all"
                  onClick={clearAll}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {'\u00D7'}
                </button>
              )}
              <div className="kst-ms__caret" />
            </>
          )}
        </div>
      </div>
      {desktopPortal}
      {mobileModal}
    </div>
  );
});

(window as any).KstMultiSelectReact = MultiSelect;
